import React, { useState, useEffect, useMemo, useRef } from 'react';
import { FaGamepad, FaUsers, FaChartLine, FaClock, FaFire } from 'react-icons/fa';
import gameService from '../../services/gameService';
import statsService from '../../services/statsService';
import styles from './SessionGallery.module.css';

const DEFAULT_QUERY = { days: 30, limit: 12, minPlays: 10 };

const SessionGallery = () => {
  const [items, setItems] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [currentTxIndex, setCurrentTxIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  // Filters
  const [query, setQuery] = useState(DEFAULT_QUERY);

  // Per-game maps (only what we show to the client)
  const [playedMap, setPlayedMap] = useState(new Map());       // basic activity
  const [userFavorMap, setUserFavorMap] = useState(new Map()); // RTP, hit, max_win...

  // Tops
  const [topPlayedIds, setTopPlayedIds] = useState(new Set());
  const [topPopularIds, setTopPopularIds] = useState(new Set());
  const [topUserIds, setTopUserIds] = useState(new Set());

  const txIntervalRef = useRef(null);

  const fetchAll = async (q = query) => {
    try {
      setBusy(true);
      setError(null);

      const sessions = await gameService.getAllSessionsWithGames();

      const [mostPlayed, mostPopular] = await Promise.all([
        statsService.getMostPlayedGames({ days: q.days, limit: 1000 }),
        statsService.getMostPopularGames({ days: q.days, limit: 1000 })
      ]);

      // Player-friendly stats
      const bestForPlayers = await statsService.getBestGamesForPlayers({
        days: q.days, limit: 1000, minPlays: q.minPlays
      });

      // Activity map (plays, unique players, last play)
      const mp = new Map();
      (mostPlayed || []).forEach(r => {
        mp.set(+r.game_id, {
          plays: +r.plays || 0,
          unique_players: +r.unique_players || 0,
          last_play: r.last_play
        });
      });
      (mostPopular || []).forEach(r => {
        const gid = +r.game_id;
        const prev = mp.get(gid) || {};
        mp.set(gid, {
          ...prev,
          unique_players: +r.unique_players || prev.unique_players || 0,
          last_play: r.last_play || prev.last_play || null
        });
      });

      // Player-facing indicators
      const uf = new Map();
      (bestForPlayers || []).forEach(r => {
        uf.set(+r.game_id, {
          rtp_percent: r.rtp_percent != null ? Number(r.rtp_percent) : null,
          hit_rate_percent: r.hit_rate_percent != null ? Number(r.hit_rate_percent) : null,
          max_win: r.max_win != null ? Number(r.max_win) : null,
        });
      });

      // Top sets
      setTopPlayedIds(new Set((mostPlayed || []).slice(0, q.limit).map(r => +r.game_id)));
      setTopPopularIds(new Set((mostPopular || []).slice(0, q.limit).map(r => +r.game_id)));
      setTopUserIds(new Set((bestForPlayers || []).slice(0, q.limit).map(r => +r.game_id)));

      setPlayedMap(mp);
      setUserFavorMap(uf);
      setItems(sessions || []);
    } catch (err) {
      console.error('Error:', err);
      setError('Failed to load statistics.');
    } finally {
      setBusy(false);
      setLoading(false);
    }
  };

  const fetchTransactions = async () => {
    try {
      const res = await statsService.getLastTransactions();
      setTransactions(res || []);
    } catch (err) {
      console.error('Failed to fetch transactions:', err);
    }
  };

  useEffect(() => {
    setLoading(true);
    fetchAll(DEFAULT_QUERY);
    fetchTransactions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Ticker animation
  useEffect(() => {
    if (transactions.length > 0) {
      txIntervalRef.current = setInterval(() => {
        setCurrentTxIndex(prev => (prev + 1) % transactions.length);
      }, 1200);
    }
    return () => {
      if (txIntervalRef.current) clearInterval(txIntervalRef.current);
      txIntervalRef.current = null;
    };
  }, [transactions]);

  const handleTickerPause = () => {
    if (txIntervalRef.current) clearInterval(txIntervalRef.current);
    txIntervalRef.current = null;
  };
  const handleTickerResume = () => {
    if (!txIntervalRef.current && transactions.length > 0) {
      txIntervalRef.current = setInterval(() => {
        setCurrentTxIndex(prev => (prev + 1) % transactions.length);
      }, 1200);
    }
  };

  // Helpers (no currency symbol)
  const formatAmount = n =>
    (n === null || n === undefined || isNaN(n))
      ? '—'
      : new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
          .format(Number(n));

  const formatSignedAmount = n => {
    if (n === null || n === undefined || isNaN(n)) return '—';
    const num = Number(n);
    const abs = Math.abs(num);
    const formatted = new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(abs);
    const sign = num > 0 ? '+' : num < 0 ? '-' : '';
    return `${sign}${formatted}`;
  };

  const formatNumber = n =>
    (n === null || n === undefined || isNaN(n))
      ? '—'
      : new Intl.NumberFormat('en-US').format(Number(n));

  const formatPercent = n =>
    (n === null || n === undefined || isNaN(n))
      ? '—'
      : `${Number(n).toFixed(2)}%`;

  const timeAgo = dateStr => {
    const d = new Date(dateStr);
    if (!d.getTime()) return '—';
    const diff = Date.now() - d.getTime();
    const mins = Math.floor(diff / 60000);
    const hours = Math.floor(mins / 60);
    const days = Math.floor(hours / 24);
    return days > 0 ? `${days} d ago` : hours > 0 ? `${hours} h ago` : mins > 0 ? `${mins} min ago` : 'now';
  };

  // Merge per-game data
  const enhancedItems = useMemo(() => {
    return items.map(s => {
      const gid = Number(s.game_id ?? s.id);
      return {
        ...s,
        gid,
        activity: playedMap.get(gid) || { plays: 0, unique_players: 0, last_play: null },
        userFavor: userFavorMap.get(gid) || {
          rtp_percent: null, hit_rate_percent: null, max_win: null
        }
      };
    });
  }, [items, playedMap, userFavorMap]);

  const handleCardClick = url_game => (window.location.href = `/${url_game}`);

  if (loading) return <div className={styles.wrap}><p className={styles.loading}>Loading games…</p></div>;
  if (error) return <div className={styles.wrap}><p className={styles.error}>{error}</p></div>;

  const currentTx = transactions[currentTxIndex];

  return (
    <div className={styles.wrap}>
      {/* Filters */}
      <div className={styles.filtersBar} role="region" aria-label="Filters">
        <div className={styles.filtersLeft}>
          <label className={styles.selectGroup}>
            <span className={styles.labelText}>Days</span>
            <select
              value={query.days}
              onChange={(e) => setQuery(q => ({ ...q, days: Number(e.target.value) }))}
              aria-label="Days range"
            >
              {[1,7,14,30,60,90].map(d => <option key={d} value={d}>{d}</option>)}
            </select>
          </label>
          <label className={styles.selectGroup}>
            <span className={styles.labelText}>Top</span>
            <select
              value={query.limit}
              onChange={(e) => setQuery(q => ({ ...q, limit: Number(e.target.value) }))}
              aria-label="Max games to show"
            >
              {[6,12,24,48].map(n => <option key={n} value={n}>{n}</option>)}
            </select>
          </label>
          <label className={styles.selectGroup} title="Minimum bets to include a game in the player-friendly ranking">
            <span className={styles.labelText}>Min. plays</span>
            <select
              value={query.minPlays}
              onChange={(e) => setQuery(q => ({ ...q, minPlays: Number(e.target.value) }))}
              aria-label="Minimum plays"
            >
              {[5,10,25,50,100].map(n => <option key={n} value={n}>{n}</option>)}
            </select>
          </label>
        </div>
        <div className={styles.filtersRight}>
          <button
            className={styles.btnRefresh}
            disabled={busy}
            onClick={() => fetchAll(query)}
            aria-live="polite"
          >
            {busy ? 'Refreshing…' : 'Refresh'}
          </button>
          <button
            className={styles.btnReset}
            disabled={busy}
            onClick={() => { setQuery(DEFAULT_QUERY); fetchAll(DEFAULT_QUERY); }}
          >
            Reset
          </button>
        </div>
      </div>

      {/* Animated transaction feed */}
      <div className={styles.transactionFeed} role="region" aria-label="Latest transactions">
        <div className={styles.transactionFeedTitle}>Latest Transactions</div>
        <div
          className={styles.transactionFeedContent}
          onMouseEnter={handleTickerPause}
          onMouseLeave={handleTickerResume}
        >
          <div className={styles.ticker}>
            {currentTx && (() => {
              const amtNum = Number(currentTx.amount);
              const rowClass =
                amtNum < 0 ? styles.tickerNeg : amtNum > 0 ? styles.tickerPos : '';
              return (
                <div
                  key={currentTx.id}
                  className={`${styles.tickerItem} ${styles.slideUp} ${rowClass}`}
                >
                  🎮 <strong className={styles.ellipsis}>{currentTx.game_name}</strong>
                  {' '}—{' '}
                  👤 <strong className={styles.ellipsis}>{currentTx.player}</strong>
                  {' '}—{' '}
                  💰 <strong className={styles.amount}>
                    {formatSignedAmount(currentTx.amount)}
                  </strong>
                  {' '}—{' '}
                  🕒 {timeAgo(currentTx.created_at)}
                </div>
              );
            })()}
          </div>
        </div>
      </div>

      {/* Game cards */}
      <div className={styles.sessionGallery}>
        {enhancedItems.length === 0 ? (
          <p>No games available.</p>
        ) : (
          enhancedItems.map(({ gid, status, image_url, url_game, game_name, session_name, activity, userFavor }) => {
            const isTopPlayed = topPlayedIds.has(gid);
            const isTopPopular = topPopularIds.has(gid);
            const isTopUser = topUserIds.has(gid);

            const badges = [
              isTopPlayed && { key: 'played', icon: <FaFire />, text: 'Top played', cls: styles.played },
              isTopPopular && { key: 'popular', icon: <FaUsers />, text: 'Top popular', cls: styles.popular },
              isTopUser && { key: 'user', icon: <FaChartLine />, text: 'Top player', cls: styles.user },
            ].filter(Boolean);

            return (
              <div
                key={gid}
                className={styles.session}
                onClick={() => handleCardClick(url_game)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && handleCardClick(url_game)}
              >
                {/* Safe header for labels/badges (prevents overlap) */}
                <div className={styles.overlayTop}>
                  <div className={styles.statusWrap}>
                    {status && (
                      <span className={styles.statusPill} data-status={String(status)}>
                        {String(status).replace('_', ' ')}
                      </span>
                    )}
                  </div>
                  <div className={styles.badgesRow}>
                    {badges.map(b => (
                      <span key={b.key} className={`${styles.topBadge} ${b.cls}`}>
                        {b.icon} {b.text}
                      </span>
                    ))}
                  </div>
                </div>

                <img src={image_url} alt={game_name} className={styles.gameImage} />

                <div className={styles.gameTitleContainer}>
                  <h3 className={`${styles.gameTitle} ${styles.ellipsis}`} title={game_name}>{game_name}</h3>
                  <FaGamepad className={styles.gameIcon} aria-hidden />
                </div>

                <div className={styles.gameCategoryContainer}>
                  <p className={`${styles.gameCategory} ${styles.ellipsis}`} title={session_name}>{session_name}</p>
                </div>

                {/* Player-facing metrics */}
                <div className={styles.statsRow}>
                  <div className={styles.statPill}><FaChartLine /> <span>{formatNumber(activity.plays)}</span></div>
                  <div className={`${styles.statPill} ${styles.muted}`}><FaClock /> <span>{timeAgo(activity.last_play)}</span></div>

                  <div className={styles.statPill}>
                    <span className={styles.statLabel}>RTP</span> <span>{formatPercent(userFavor.rtp_percent)}</span>
                  </div>
                  <div className={styles.statPill}>
                    <span className={styles.statLabel}>Hit</span> <span>{formatPercent(userFavor.hit_rate_percent)}</span>
                  </div>
                  <div className={styles.statPill}>
                    <span className={styles.statLabel}>Max</span> <span>{formatAmount(userFavor.max_win)}</span>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default SessionGallery;
